import React, { useEffect, useState } from "react";
import { Plus, X, GripVertical, ChevronDown, ChevronUp, Save } from "lucide-react";
import { toast }            from "../../../ui/Toast";
import { useConfirmDelete } from "../../../../hooks/useConfirmDelete";

const API     = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card    = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls= "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls= "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const MODULES  = ["Leave","Expense","Onboarding","Offboarding","Salary Revision","Promotion","Asset Request","Custom"];
const ROLES    = ["employee","manager","hr","admin","finance","cfo","executive"];
const STEP_EMPTY = { role: "manager", label: "", escalate_after_hours: 24, required: true };

const EMPTY_WF = { name: "", module: "Leave", description: "", steps: [{ ...STEP_EMPTY }] };

/* THE "APPROVAL CHAINS" TAB. Renamed from "Workflows" — display strings
 * only, and only the three that named the wrong thing.
 *
 * NOTHING ELSE IN THIS FILE MOVED. Same `workflows` table, same
 * /api/admin/workflows routes, same behaviour, same component name. The
 * table and route keep the old name deliberately: renaming them is a
 * migration and a breaking API change for something that works, and D2
 * defers both.
 *
 * WHAT THIS ACTUALLY IS, since the old name obscured it: an ordered list
 * of approvers — role, label, escalate_after_hours, required — attached
 * to a module. It routes sign-off. It does not model a process, and it
 * is not the workflow catalogue on the tab to the left. */
export default function WorkflowsTab() {
  const [workflows, setWorkflows] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [editing,   setEditing]   = useState(null);
  const [draft,     setDraft]     = useState(EMPTY_WF);
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [expanded,  setExpanded]  = useState(null);
  const [msg,       setMsg]       = useState(null);
  const { confirmDelete, ConfirmDialog } = useConfirmDelete();
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/workflows`, { headers: { Authorization: `Bearer ${token}` } });
      setWorkflows(Array.isArray(await res.json()) ? await res.json() : []);
    } catch { setWorkflows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!draft.name || !draft.steps.length) return;
    setSaving(true); setMsg(null);
    try {
      const isNew = !editing;
      const res = await fetch(
        isNew ? `${API}/api/admin/workflows` : `${API}/api/admin/workflows/${editing}`,
        { method: isNew ? "POST" : "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(draft) }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg({ type: "success", text: "✓ Workflow saved." });
      setShowForm(false); setDraft(EMPTY_WF); setEditing(null);
      await load();
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setSaving(false); }
  };

  const del = (id) => confirmDelete("this workflow", async () => {
    try {
      const res = await fetch(`${API}/api/admin/workflows/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setWorkflows(w => w.filter(x => x.id !== id));
      toast.success("Workflow deleted");
    } catch {
      toast.error("Failed to delete workflow");
    }
  });

  const startEdit = (w) => { setDraft({ ...w, steps: w.steps || [{ ...STEP_EMPTY }] }); setEditing(w.id); setShowForm(true); };

  const addStep  = () => setDraft(d => ({ ...d, steps: [...d.steps, { ...STEP_EMPTY }] }));
  const remStep  = (i) => setDraft(d => ({ ...d, steps: d.steps.filter((_, idx) => idx !== i) }));
  const setStep  = (i, k, v) => setDraft(d => ({ ...d, steps: d.steps.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const moveStep = (i, dir) => {
    const ni = i + dir;
    if (ni < 0 || ni >= draft.steps.length) return;
    const steps = [...draft.steps];
    [steps[i], steps[ni]] = [steps[ni], steps[i]];
    setDraft(d => ({ ...d, steps }));
  };

  return (
    <div className="space-y-5">

      {/* Workflow builder */}
      {showForm && (
        <div className={`${card} p-5 space-y-5`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
            {editing ? "Edit Workflow" : "New Approval Workflow"}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Workflow Name</label>
              <input className={inputCls} value={draft.name} onChange={e => setDraft(d => ({...d, name: e.target.value}))} placeholder="e.g. Leave Approval Flow" />
            </div>
            <div>
              <label className={labelCls}>Module</label>
              <select className={inputCls} value={draft.module} onChange={e => setDraft(d => ({...d, module: e.target.value}))}>
                {MODULES.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Description</label>
              <input className={inputCls} value={draft.description || ""} onChange={e => setDraft(d => ({...d, description: e.target.value}))} />
            </div>
          </div>

          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider">Approval Steps</p>
              <button onClick={addStep}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                <Plus size={12} /> Add Step
              </button>
            </div>
            <div className="space-y-3">
              {draft.steps.map((s, i) => (
                <div key={i} className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => moveStep(i, -1)} disabled={i === 0} className="text-white/20 hover:text-white disabled:opacity-20 transition-colors"><ChevronUp size={12} /></button>
                      <button onClick={() => moveStep(i, 1)} disabled={i === draft.steps.length - 1} className="text-white/20 hover:text-white disabled:opacity-20 transition-colors"><ChevronDown size={12} /></button>
                    </div>
                    <div className="w-6 h-6 rounded-full bg-[#00ff90]/15 text-[#00ff90] text-xs flex items-center justify-center font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 grid grid-cols-3 gap-3">
                      <div>
                        <label className={labelCls}>Step Label</label>
                        <input className={inputCls} value={s.label || ""} onChange={e => setStep(i, "label", e.target.value)} placeholder="Manager Approval" />
                      </div>
                      <div>
                        <label className={labelCls}>Approver Role</label>
                        <select className={inputCls} value={s.role} onChange={e => setStep(i, "role", e.target.value)}>
                          {ROLES.map(r => <option key={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Escalate after (hrs)</label>
                        <input type="number" className={inputCls} value={s.escalate_after_hours || 24}
                          onChange={e => setStep(i, "escalate_after_hours", Number(e.target.value))} />
                      </div>
                    </div>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs text-white/40 shrink-0">
                      <input type="checkbox" checked={!!s.required} onChange={e => setStep(i, "required", e.target.checked)} className="w-3.5 h-3.5 accent-[#00ff90]" />
                      Required
                    </label>
                    {draft.steps.length > 1 && (
                      <button onClick={() => remStep(i)} className="text-white/20 hover:text-red-400 transition-colors shrink-0"><X size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {draft.steps.length > 1 && (
              <p className="text-xs text-white/25 mt-2">
                Flow: {draft.steps.map((s, i) => s.label || `Step ${i+1}`).join(" → ")}
              </p>
            )}
          </div>

          {msg && <p className={`text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}
          <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
            <button onClick={() => { setShowForm(false); setDraft(EMPTY_WF); setEditing(null); }}
              className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={save} disabled={saving || !draft.name}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Saving…" : "Save Workflow"}
            </button>
          </div>
        </div>
      )}

      {/* Workflow list */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Approval chains</span>
          <button onClick={() => { setDraft(EMPTY_WF); setEditing(null); setShowForm(v => !v); }}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> New Workflow
          </button>
        </div>
        <div className="divide-y divide-white/5">
          {loading ? Array.from({length:3}).map((_,i) => <div key={i} className="px-5 py-4"><div className="h-10 rounded-lg bg-white/5 animate-pulse" /></div>)
          /* AN EMPTY STATE THAT STATES AN ABSENCE RATHER THAN LOOKING LIKE
             A FAULT. "No workflows configured" on a tab called Workflows
             was read as the feature being broken; it was neither broken
             nor the feature the reader was looking for. */
          : workflows.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-white/25 text-sm">
                No approval chains yet. A chain is an ordered list of
                approvers for one module — who signs off leave, an expense
                claim or a salary revision, and when it escalates.
              </p>
              <p className="text-white/20 text-xs mt-1.5">
                Looking for the catalogue of HR processes instead? That is
                the Workflows tab.
              </p>
            </div>
          )
          : workflows.map(w => (
            <div key={w.id}>
              <div className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-white/3 transition-colors cursor-pointer"
                onClick={() => setExpanded(expanded === w.id ? null : w.id)}>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm text-white/75 font-medium">{w.name}</p>
                    <span className="text-xs text-[#00ff90]/50 border border-[#00ff90]/15 px-2 py-0.5 rounded-full">{w.module}</span>
                    <span className="text-xs text-white/25">{(w.steps||[]).length} steps</span>
                  </div>
                  {w.description && <p className="text-xs text-white/30">{w.description}</p>}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={e => { e.stopPropagation(); startEdit(w); }}
                    className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                    <Plus size={12} />
                  </button>
                  <button onClick={e => { e.stopPropagation(); del(w.id); }}
                    className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                    <X size={12} />
                  </button>
                </div>
              </div>
              {expanded === w.id && (w.steps||[]).length > 0 && (
                <div className="px-5 pb-4 flex items-center gap-2 flex-wrap">
                  {w.steps.map((s, i) => (
                    <React.Fragment key={i}>
                      <div className="rounded-lg border border-white/5 bg-white/3 px-3 py-2 text-center">
                        <p className="text-xs text-white/60 font-medium">{s.label || `Step ${i+1}`}</p>
                        <p className="text-xs text-white/30">{s.role}</p>
                        <p className="text-xs text-white/20">{s.escalate_after_hours}h escalate</p>
                      </div>
                      {i < w.steps.length - 1 && <span className="text-white/20 text-lg">→</span>}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {ConfirmDialog}
    </div>
  );
}